import React, { useContext, useEffect, useState } from "react";
import Sidebar from "react-sidebar";
import { useNavigate } from "react-router-dom";
import { BsBellFill, BsCart2 } from "react-icons/bs";
import { Badge, Button, Col, Row } from "reactstrap";
import { CartCurrent } from "./";
import { api, helpers, UserContext } from "../utils";

export default function CartMenu() {
   const userCtx = useContext(UserContext);
   const navigate = useNavigate();
   const recallVal = 60000;  // 1 minutes - the amount of time between intervals (1 mins * 60 seconds * 1000 milliseconds)
   const resetVal = 59950;   // 0.95 minutes - the amount of time to reset a called interval
   const [called, setCalled] = useState(0); // incremented when the interval function runs
   const [fetching, setFetching] = useState(false); // true if fetching task count
   const [tasks, setTasks] = useState(0);   // number of open tasks for the current user

   useEffect(() => {
      const interval = setInterval(function () { setCalled(called + 1); }, recallVal);
      getTasks();
      return () => clearInterval(interval);
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [])

   useEffect(() => {
      if (fetching) return;
      if (called === 1) {
         getTasks();
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [called])

   function getTasks() {
      // The task/notification feature is internal-only (GetTaskCount is AnyNonCustomer - it counts
      // process-change and CoA-print tasks). Customer-role users have none, so skip the call; otherwise
      // it 403s on every poll and the global handler toasts "no permission" (real customers AND impersonation).
      // Skip for the same reason when there is no signed-in user: a missing or EXPIRED session polls a
      // 401, which the axios interceptor answers by clearing storage and redirecting to sign-in, so the
      // poll can only add console noise to a sign-out that is already happening. The customer check
      // alone does NOT cover this - `currentUser?.isAnyCustomerTypeRole` is undefined (falsy) when
      // currentUser is null, so the call went out anyway.
      // A signed-in account with NO roles is skipped too: the server 403s every action for it
      // (RoleRequiredFilter), and the poll below keeps running even though the render returns null.
      const user = userCtx.currentUser;
      if (!user?.id || user.isAnyCustomerTypeRole || helpers.hasNoRoles(user)) {
         setTasks(0);
         return;
      }
      setFetching(true);
      setTimeout(() => {
         setFetching(false);
         setCalled(0);
      }, resetVal);

      //console.log("getting task count");
      api
         .fetch(`Reference/GetTaskCount`)
         .then(response => {
            // Optional-chain the shape deliberately: the shared axios error interceptor RESOLVES failed
            // requests instead of rejecting them (api.js returns the error rather than Promise.reject),
            // so a 4xx/5xx arrives HERE as an AxiosError - which has no .data - and .catch below never
            // runs. Reading response.data.success directly threw a TypeError. Treat any unexpected
            // shape as "no tasks" rather than crashing the notification bell.
            if (response?.data?.success) {
               setTasks(response.data.tasks);
            }
            else {
               setTasks(0);
            }
         })
         .catch((error) => {
            api.catchHandler(error);
            setTasks(0);
         })
   }

   function goHome() {
      navigate(`/`);
   }

   function proceedToCheckout() {
      userCtx.setCartMenuOpen(false);
      navigate(`/order`);
   }

   if (
      !userCtx ||
      !userCtx.currentUser ||
      helpers.hasNoRoles(userCtx.currentUser) || // no cart or tasks to offer an account with no roles
      userCtx.onMobile ||
      userCtx.onMobile === undefined ||
      userCtx.onMobile === null
   )
      return null;

   return (
      <Sidebar
         sidebar={
            userCtx.cartMenuOpen && (
               <CartCurrent
                  width={"300px"}
                  checkoutCallback={proceedToCheckout}
                  showActiveCarts
                  closeCartSidebar={() => userCtx.toggleCartOpen(false)}
               />
            )
         } // opened menu
         open={userCtx.cartMenuOpen}
         styles={{
            root: {
               position: "undefined",
            },
            sidebar: {
               maxWidth: "1280px",
               width: "100%",
               height: "100%",
               position: "fixed",
               zIndex: 1026,
               borderTopLeftRadius: "5px",
               overflowY: "scroll",
               padding: "0px",
            },
            content: {
               position: "undefined",
            },
         }}
         pullRight={true}
         onSetOpen={() => userCtx.toggleCartOpen(!userCtx.cartMenuOpen)}
      >
         <Row className="cart-menu-toggle p-0">
            <Col className="p-0 transparent-background">
               {tasks > 0 ?
                  <Button title="Tasks" onClick={() => goHome()} className="px-2 l2BrightBlue">
                     <BsBellFill className="cart-menu-button align-self-center" />
                     <Badge color="info" pill>{tasks}</Badge>
                  </Button>
                  : null
               }
               <Button
                  title="Open/Close Cart"
                  onClick={() => userCtx.toggleCartOpen(!userCtx.cartMenuOpen)}
                  className="px-2 l2BrightBlue"
               >
                  <BsCart2 className="cart-menu-button align-self-center" />
                  <Badge
                     color="info"
                     children={
                        (userCtx.shoppingCart &&
                           userCtx.shoppingCart.cartItems &&
                           userCtx.shoppingCart.cartItems.length) ||
                        "0"
                     }
                     pill
                  />
               </Button>
            </Col>
         </Row>
      </Sidebar>
   );
}
